/** Presentation-only selection for the bottom combat readout. The hero's
 * standing order wins; otherwise show an enemy currently engaging them. */
import { TICK_MS } from "@pd/shared";
import type { Observation, ObservedEnemy } from "@pd/shared";

export function combatFocus(obs: Pick<Observation, "players" | "enemies">, playerId: number): ObservedEnemy | null {
  const me = obs.players.find((p) => p.id === playerId);
  if (!me?.alive) return null;
  const chosen = obs.enemies.find((e) => e.id === me.attackTarget && e.alive);
  if (chosen) return chosen;
  // Several NPCs may engage at once. Show the nearest, breaking ties by id,
  // so the readout stays stable and never depends on array iteration order.
  const engaging = obs.enemies.filter((e) => e.alive &&
    (e.targetId === playerId || (e.attack?.targetIsEnemy === false && e.attack.targetId === playerId)));
  engaging.sort((a, b) => {
    const da = Math.abs(a.x - me.x) + Math.abs(a.y - me.y);
    const db = Math.abs(b.x - me.x) + Math.abs(b.y - me.y);
    return da - db || a.id - b.id;
  });
  return engaging[0] ?? null;
}

/** The server's elapsed floor ticks, not a client-side wall clock. */
export function floorTime(tick: number): string {
  const seconds = Math.floor(tick * TICK_MS / 1000);
  const minutes = Math.floor(seconds / 60);
  const ss = String(seconds % 60).padStart(2, "0");
  if (minutes < 60) return `${String(minutes).padStart(2, "0")}:${ss}`;
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}:${ss}`;
}
